// Batch close accounting for the Hades board. Pure — no component, no axios —
// because the interesting part of a bulk close is not the requests, it is what
// you tell the person afterwards, and that wants a test rather than a screenshot.
//
// WHY THE ROUTING IS NOT IN HERE: which endpoint a batch goes to is decided by
// the destination status (see Support_Hades._bulkSetStatus). This module only
// reads the answers a close came back with.

// One requirement's outcome. `closed` is not returned by any function here —
// the caller knows a success when it sees one — but it is named so the buckets
// read as one vocabulary at the call site.
export const CLOSED = 'closed';
export const STALE = 'stale';
export const ALREADY = 'already';
export const FAILED = 'failed';

// POST /api/hades/close/ returns 409 for TWO different reasons and the answer
// changes what a person does next: a stale verdict is retryable with
// acknowledge_stale, an already-done requirement is not — there is nothing to
// retry, the work is already where you were sending it.
//
// TOLD APART BY THE PAYLOAD, NOT BY MATCHING THE SENTENCE. The stale branch is
// the only one that reports what it read and when
// (investigated_at / requirement_updated_at); matching on
// 'The requirement changed after…' would break the moment that string is
// reworded, and it would break silently — a stale row would be filed as an
// unretryable failure and the retry button would not appear.
export function closeFailureKind(error) {
  const response = error?.response;
  if (response?.status !== 409) return FAILED;
  return response.data?.requirement_updated_at ? STALE : ALREADY;
}

export function closeFailureDetail(error) {
  return error?.response?.data?.detail || 'Could not close the requirement.';
}

export function emptyCloseResult(status) {
  return { status, closed: [], stale: [], already: [], failed: [] };
}

// ONE SENTENCE, WITH THE BUCKETS NAMED. `_bulkPatch`'s "N of M updates failed"
// is fine for a due-date sweep and wrong here: ten selected rows that did not
// all close leave a person needing to know WHICH and WHY, because a stale
// verdict is a retry and an already-closed requirement is nothing to do. The
// ids themselves are listed by the caller; this is the headline above them.
export function bulkCloseSummary(result) {
  const attempted = result.closed.length + result.stale.length
    + result.already.length + result.failed.length;
  const parts = [`Closed ${result.closed.length} of ${attempted}`];
  if (result.stale.length) {
    parts.push(`${result.stale.length} changed since Hades looked`);
  }
  if (result.already.length) {
    parts.push(`${result.already.length} already closed`);
  }
  if (result.failed.length) parts.push(`${result.failed.length} failed`);
  return `${parts.join(' · ')}.`;
}
